import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { getComicById, comics } from '@/mocks/comics';
import { useState } from 'react';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const comic = getComicById(id || '');
  const [activeTab, setActiveTab] = useState<'info' | 'chapters'>('info');

  if (!comic) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <ScrollToTop />
        <div className="pt-20 pb-20 text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-900">作品未找到</h1>
          <Link to="/" className="text-primary-500 hover:text-primary-600 mt-4 inline-block">返回首页</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const relatedComics = comics
    .filter(c => c.categorySlug === comic.categorySlug && c.id !== comic.id)
    .slice(0, 4);

  const chapterList = comic.chapterList || Array.from({ length: Math.min(comic.chapters, 10) }, (_, i) => ({
    id: `ch-${comic.id}-${i + 1}`,
    title: `第${i + 1}话`,
    number: i + 1,
    date: '2026-08-01',
  }));

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: comic.title,
            author: { '@type': 'Person', name: comic.author },
            datePublished: '2026-01-01',
            dateModified: comic.updateDate,
            description: comic.description,
            url: `https://hmanga.com/article/${comic.id}`,
            isPartOf: { '@type': 'WebSite', name: 'h漫画', url: 'https://hmanga.com' },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://hmanga.com/' },
              { '@type': 'ListItem', position: 2, name: comic.category, item: `https://hmanga.com/category/${comic.categorySlug}` },
              { '@type': 'ListItem', position: 3, name: comic.title },
            ],
          }),
        }}
      />

      <div className="pt-20">
        <Breadcrumb
          items={[
            { label: '首页', path: '/' },
            { label: comic.category, path: `/category/${comic.categorySlug}` },
            { label: comic.title },
          ]}
        />

        <section className="w-full px-4 md:px-6 py-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="w-full lg:w-[280px] flex-shrink-0">
                <div className="sticky top-24">
                  <div className="rounded-lg overflow-hidden border border-background-200">
                    <div className="h-[400px] overflow-hidden">
                      <img
                        src={comic.cover}
                        alt={comic.title}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  </div>
                  <Link
                    to={`/category/${comic.categorySlug}`}
                    className="block w-full mt-3 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-md text-sm font-medium text-center transition-colors whitespace-nowrap cursor-pointer"
                  >
                    开始阅读
                  </Link>
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                    {comic.title}
                  </h1>
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                      comic.status === '连载中'
                        ? 'bg-accent-100 text-accent-700'
                        : 'bg-secondary-100 text-secondary-700'
                    }`}
                  >
                    {comic.status}
                  </span>
                </div>

                <p className="text-sm text-foreground-600 mb-4">
                  <strong>作者：</strong>{comic.author}
                  <span className="mx-3 text-foreground-300">|</span>
                  <strong>分类：</strong>
                  <Link to={`/category/${comic.categorySlug}`} className="text-primary-500 hover:text-primary-600">
                    {comic.category}
                  </Link>
                </p>

                <div className="flex flex-wrap items-center gap-4 mb-6 text-sm text-foreground-500">
                  <span className="flex items-center gap-1">
                    <i className="ri-star-fill text-accent-500 w-4 h-4 flex items-center justify-center"></i>
                    <strong className="text-foreground-800">{comic.rating}</strong> 分
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line w-4 h-4 flex items-center justify-center"></i>
                    {comic.views > 10000 ? `${(comic.views / 10000).toFixed(1)}万` : comic.views} 次浏览
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-file-list-3-line w-4 h-4 flex items-center justify-center"></i>
                    共 <strong className="text-foreground-800">{comic.chapters}</strong> 话
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line w-4 h-4 flex items-center justify-center"></i>
                    更新于 {comic.updateDate}
                  </span>
                </div>

                {comic.tags && (
                  <div className="flex items-center gap-2 mb-6 flex-wrap">
                    {comic.tags.map((tag) => (
                      <span key={tag} className="px-3 py-1 bg-secondary-100 text-secondary-700 rounded-full text-xs whitespace-nowrap">
                        {tag}
                      </span>
                    ))}
                  </div>
                )}

                <div className="flex items-center gap-0 border-b border-background-200 mb-6">
                  <button
                    onClick={() => setActiveTab('info')}
                    className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap cursor-pointer ${
                      activeTab === 'info'
                        ? 'border-primary-500 text-primary-500'
                        : 'border-transparent text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    作品简介
                  </button>
                  <button
                    onClick={() => setActiveTab('chapters')}
                    className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap cursor-pointer ${
                      activeTab === 'chapters'
                        ? 'border-primary-500 text-primary-500'
                        : 'border-transparent text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    章节列表 ({comic.chapters})
                  </button>
                </div>

                {activeTab === 'info' ? (
                  <div className="text-sm text-foreground-700 leading-relaxed">
                    <p>{comic.description}</p>
                    <p className="mt-4">
                      本作品为h漫画平台独家连载作品，讲述了一段令人心动的故事。
                      作者{comic.author}以其独特的画风和细腻的情感刻画，赢得了广大读者的喜爱。
                      作品自连载以来，持续保持高人气，是{comic.category}中不可错过的佳作。
                      如果您喜欢这种风格的作品，欢迎收藏并持续关注更新。
                    </p>
                  </div>
                ) : (
                  <div className="flex flex-col gap-2">
                    {chapterList.map((ch) => (
                      <div
                        key={ch.id}
                        className="flex items-center justify-between p-3 bg-background-50 rounded-md border border-background-200 hover:border-primary-300 transition-colors cursor-pointer"
                      >
                        <span className="text-sm font-medium text-foreground-800">{ch.title}</span>
                        <span className="text-xs text-foreground-500">{ch.date}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {relatedComics.length > 0 && (
          <section className="w-full px-4 md:px-6 py-10 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-xl font-heading font-bold text-foreground-900 mb-6">
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {relatedComics.map((c) => (
                  <ComicCard key={c.id} comic={c} size="small" />
                ))}
              </div>
            </div>
          </section>
        )}
      </div>

      <Footer />
    </div>
  );
}